import React, { Component } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Loading, Box } from '@cubeyou-team/hx-react-components';
import {
  getTokens,
  isAccessTokenValid,
  useSsoOptional,
} from '@discover-frontend/auth';
import { useTrialBootstrapGate } from 'src/modules/free-trial/hooks/use-trial-bootstrap-gate';
import { useGetAllUserProducts } from 'src/containers/HomeContainer/api/hooks/use-get-all-user-products';
import { isTrialUserFromToken } from 'src/utils/AuthHelperMethods';
import { initialAuth } from '../../actions/AuthActions';
import { requestMe } from '../../actions/UsersActions';
import { syncSsoTokens } from '../../utils/syncSsoTokens';
import { getAccountsSessionReady } from '../../utils/getAccountsSessionReady';
import { NotLoggedLayout, AuthLayout } from '../../layouts';
import { ROUTES } from '../../constants/routes';

const PUBLIC_ROUTES = [
  '/login',
  '/registration',
  ROUTES.TRIAL_SIGNUP,
  ROUTES.TRIAL_START,
  '/reset-password',
  '/logout',
  '/forgot-password',
  '/error',
  '/invitation-expired',
  ROUTES.TERMS_AND_CONDITIONS,
];

class InitContainer extends Component {
  componentDidMount() {
    const isRequestingSingleSingOff = this.isRequestingSingleSingOff();
    !isRequestingSingleSingOff && this.props.initialAuth();
  }

  componentDidUpdate(prevProps) {
    const { auth } = this.props;

    if (
      auth.isLogged &&
      auth.accessToken &&
      auth.token &&
      (prevProps.auth.accessToken !== auth.accessToken ||
        prevProps.auth.token !== auth.token ||
        prevProps.auth.refreshToken !== auth.refreshToken)
    ) {
      syncSsoTokens({
        accessToken: auth.accessToken,
        idToken: auth.token,
        refreshToken: auth.refreshToken,
      });
    }

    const shouldCallMeData = this.shouldCallMeData();
    if (shouldCallMeData) {
      this.props.requestMe();
    }
  }

  isRequestingSingleSingOff() {
    return this.props.location.pathname === '/logout';
  }

  isRequestingSingleSingOn() {
    const { search } = this.props.location;
    const hasRedirectUrl = search.includes('redirectUrl');
    return this.props.location.pathname === '/login' && hasRedirectUrl;
  }

  shouldCallMeData = () => {
    const isPublicPath = this.isPublicPath();
    const { accessToken } = getTokens();

    return (
      !isPublicPath &&
      this.props.auth.isLogged &&
      this.props.canBootstrapSession &&
      isAccessTokenValid(accessToken) &&
      !this.props.me.user.email &&
      !this.props.me.isLoading
    );
  };

  canShowChildren = () => {
    const isRequestingSingleSingOn = this.isRequestingSingleSingOn();
    const isRequestingSingleSingOff = this.isRequestingSingleSingOff();
    const isPublicPath = this.isPublicPath();
    const authCheckDone = this.props.auth.init;
    const { me } = this.props;
    const hasUserData = Boolean(me.user.email);

    if (isRequestingSingleSingOff) {
      return true;
    }

    if (
      isRequestingSingleSingOn &&
      !authCheckDone &&
      !this.props.auth.hasLoggedOut
    ) {
      return false;
    }

    if (isPublicPath) {
      return true;
    }

    // Children mount their own fetches (company, products), so the trial gate
    // must hold the shell — not just the /users/me dispatch.
    if (this.props.isTrialBootstrapBlocked) {
      return false;
    }

    // Home renders its grid straight from the query cache, so waiting here
    // keeps a single spinner instead of one per bootstrap step.
    if (this.props.isHomeBootstrapBlocked) {
      return false;
    }

    // Stale-while-revalidate: keep showing content while /users/me refetches.
    return authCheckDone && (!me.isLoading || hasUserData);
  };

  isPublicPath = () => {
    return PUBLIC_ROUTES.includes(this.props.location.pathname);
  };

  render() {
    const canShowChildren = this.canShowChildren();
    if (!canShowChildren) {
      return (
        <NotLoggedLayout>
          <AuthLayout>
            <Box minHeight="200px">
              <Loading isLoading={true} />
            </Box>
          </AuthLayout>
        </NotLoggedLayout>
      );
    }
    return this.props.children;
  }
}

const mapDispatchToProps = {
  initialAuth,
  requestMe,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  me: state.me,
});

const ConnectedInitContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(InitContainer);

const InitContainerWithSession = (props) => {
  const sso = useSsoOptional();
  const sessionReady = getAccountsSessionReady(sso);
  const decodedToken = useSelector((state) => state.auth.decodedToken);

  const isPublicPath = PUBLIC_ROUTES.includes(props.location.pathname);
  const shouldGateOnTrial =
    !isPublicPath && isTrialUserFromToken(decodedToken ?? null);

  const { canBootstrapSession, isTrialBootstrapBlocked } =
    useTrialBootstrapGate({
      isTrial: shouldGateOnTrial,
      sessionReady,
    });

  const isHomeRoute = props.location.pathname === '/';
  const { isLoading: isLoadingHomeProducts } = useGetAllUserProducts(
    isHomeRoute && canBootstrapSession
  );

  return (
    <ConnectedInitContainer
      {...props}
      canBootstrapSession={canBootstrapSession}
      isTrialBootstrapBlocked={isTrialBootstrapBlocked}
      isHomeBootstrapBlocked={isHomeRoute && isLoadingHomeProducts}
    />
  );
};

export default withRouter(InitContainerWithSession);
